import React, { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Modal,
  Pagination,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  ArrowClockwise,
  Envelope,
  Eye,
  Funnel,
  Person,
  Search,
} from "react-bootstrap-icons";
import axios from "axios";
import { toast } from "react-toastify";
import API_BASE_URL from "../../../config/api";

const STATUS_OPTIONS = [
  { value: "new", label: "New", variant: "primary" },
  { value: "in_progress", label: "In Progress", variant: "warning" },
  { value: "resolved", label: "Resolved", variant: "success" },
  { value: "closed", label: "Closed", variant: "secondary" },
];

const getStatusConfig = (status) =>
  STATUS_OPTIONS.find((item) => item.value === status) || STATUS_OPTIONS[0];

const getItems = (payload) =>
  payload?.data?.contacts ||
  payload?.data?.contactUs ||
  payload?.data?.items ||
  payload?.contacts ||
  payload?.contactUs ||
  payload?.items ||
  payload?.data ||
  [];

const getTotalRecords = (payload, fallback) =>
  payload?.pagination?.totalRecords ||
  payload?.data?.pagination?.totalRecords ||
  payload?.totalRecords ||
  payload?.count ||
  fallback;

const getTotalPages = (payload, fallback) =>
  payload?.pagination?.totalPages ||
  payload?.data?.pagination?.totalPages ||
  payload?.totalPages ||
  Math.max(1, Math.ceil(getTotalRecords(payload, fallback) / 10));

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ContactUsMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showView, setShowView] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const limit = 10;

  const token =
    localStorage.getItem("adminToken") || localStorage.getItem("token");

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/api/admin/contact-us`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: currentPage,
          limit,
          status: filterStatus || undefined,
          search: searchTerm || undefined,
        },
      });

      const nextMessages = getItems(res.data);
      const normalizedMessages = Array.isArray(nextMessages) ? nextMessages : [];
      setMessages(normalizedMessages);
      setTotalRecords(getTotalRecords(res.data, normalizedMessages.length));
      setTotalPages(getTotalPages(res.data, normalizedMessages.length));
    } catch (err) {
      console.error("Fetch contact messages error:", err);
      toast.error("Failed to fetch contact messages");
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterStatus, searchTerm, token]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const updateStatus = async (message, status) => {
    const messageId = message?._id || message?.id;
    if (!messageId) {
      toast.error("Missing contact message id");
      return;
    }

    try {
      setUpdatingId(messageId);
      await axios.put(
        `${API_BASE_URL}/api/admin/contact-us/${messageId}/status`,
        { status },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      toast.success("Status updated");
      setMessages((prev) =>
        prev.map((item) =>
          (item._id || item.id) === messageId ? { ...item, status } : item
        )
      );
      setSelectedMessage((prev) =>
        prev && (prev._id || prev.id) === messageId ? { ...prev, status } : prev
      );
    } catch (err) {
      console.error("Update contact message status error:", err);
      toast.error("Failed to update status");
    } finally {
      setUpdatingId("");
    }
  };

  const renderPagination = () => {
    const items = [];
    for (let page = 1; page <= totalPages; page += 1) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => setCurrentPage(page)}
        >
          {page}
        </Pagination.Item>
      );
    }
    return items;
  };

  return (
    <div style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
      <Card className="border-0 mb-4">
        <Card.Body className="p-0">
          <Row className="align-items-center g-3">
            <Col md={3}>
              <h4 className="fw-bold mb-0">Contact Us</h4>
              <small className="text-muted">Manage public contact messages</small>
            </Col>

            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <Search />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search by name, email, or message"
                  value={searchTerm}
                  onChange={(event) => {
                    setSearchTerm(event.target.value);
                    setCurrentPage(1);
                  }}
                />
              </InputGroup>
            </Col>

            <Col md={3}>
              <InputGroup>
                <InputGroup.Text>
                  <Funnel />
                </InputGroup.Text>
                <Form.Select
                  value={filterStatus}
                  onChange={(event) => {
                    setFilterStatus(event.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">All Status</option>
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </Form.Select>
              </InputGroup>
            </Col>

            <Col md={2} className="text-md-end">
              <Button variant="outline-primary" onClick={fetchMessages}>
                <ArrowClockwise className="me-2" />
                Refresh
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2 text-muted">Loading messages...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-5">
              <Envelope size={42} className="text-muted mb-3" />
              <h5>No contact messages found</h5>
              <p className="text-muted mb-0">New messages will show here.</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Message</th>
                      <th>Status</th>
                      <th>Received</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {messages.map((message) => {
                      const messageId = message._id || message.id;
                      const statusConfig = getStatusConfig(message.status);

                      return (
                        <tr key={messageId}>
                          <td>
                            <Person className="me-1" />
                            {message.name || "-"}
                          </td>
                          <td>
                            <Envelope className="me-1" />
                            {message.email || "-"}
                          </td>
                          <td style={{ maxWidth: 360 }}>
                            <div className="text-truncate">
                              {message.message || "-"}
                            </div>
                          </td>
                          <td>
                            <Badge bg={statusConfig.variant}>
                              {statusConfig.label}
                            </Badge>
                          </td>
                          <td>
                            <small>
                              {formatDate(message.createdAt || message.updatedAt)}
                            </small>
                          </td>
                          <td>
                            <div className="d-flex gap-2 align-items-center">
                              <Button
                                size="sm"
                                variant="primary"
                                onClick={() => {
                                  setSelectedMessage(message);
                                  setShowView(true);
                                }}
                                title="View message"
                              >
                                <Eye />
                              </Button>
                              <Form.Select
                                size="sm"
                                value={message.status || "new"}
                                onChange={(event) =>
                                  updateStatus(message, event.target.value)
                                }
                                disabled={updatingId === messageId}
                                style={{ width: 140 }}
                              >
                                {STATUS_OPTIONS.map((status) => (
                                  <option key={status.value} value={status.value}>
                                    {status.label}
                                  </option>
                                ))}
                              </Form.Select>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              <div className="p-3 d-flex justify-content-between align-items-center">
                <small className="text-muted">
                  Showing {(currentPage - 1) * limit + 1}-
                  {Math.min(currentPage * limit, totalRecords)} of {totalRecords}
                </small>
                {totalPages > 1 && (
                  <Pagination className="mb-0">
                    <Pagination.Prev
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    />
                    {renderPagination()}
                    <Pagination.Next
                      disabled={currentPage === totalPages}
                      onClick={() =>
                        setCurrentPage((page) => Math.min(totalPages, page + 1))
                      }
                    />
                  </Pagination>
                )}
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      <Modal show={showView} onHide={() => setShowView(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Contact Message</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMessage && (
            <>
              <Row className="g-3 mb-4">
                <Col md={6}>
                  <strong>Name</strong>
                  <div>{selectedMessage.name || "-"}</div>
                </Col>
                <Col md={6}>
                  <strong>Email</strong>
                  <div>{selectedMessage.email || "-"}</div>
                </Col>
                <Col md={6}>
                  <strong>Status</strong>
                  <div className="mt-1">
                    <Form.Select
                      value={selectedMessage.status || "new"}
                      onChange={(event) =>
                        updateStatus(selectedMessage, event.target.value)
                      }
                      disabled={
                        updatingId === (selectedMessage._id || selectedMessage.id)
                      }
                    >
                      {STATUS_OPTIONS.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                </Col>
                <Col md={6}>
                  <strong>Received</strong>
                  <div>
                    {formatDate(selectedMessage.createdAt || selectedMessage.updatedAt)}
                  </div>
                </Col>
              </Row>

              <strong>Message</strong>
              <div className="border rounded bg-light p-3 mt-2" style={{ whiteSpace: "pre-wrap" }}>
                {selectedMessage.message || "-"}
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ContactUsMessages;
